"use client";

import * as React from "react";

import { cn } from "../../cn";
import type { MentionItem, SlashCommand } from "../chat/types";
import { Skeleton } from "../skeleton";
import type { DocLinks, PageLink } from "./doc-link";
import type { PageDocument } from "./page-editor-body";

export type { PageDocument };

// A page (spec M7-02: page, SOP, policy, task body, vision, journal, decision, strategy): a
// textarea-style title (Work OS task title: 28 / 650 on pages, 20 / 600 in the task drawer;
// Enter moves into the body, Escape reverts) over the BlockNote body. Prose width 760 unless
// `width="full"`. The body loads on first render; a skeleton holds its place until then.

const PageEditorBody = React.lazy(() => import("./page-editor-body"));

const NONE: never[] = [];

export type PageEditorProps = Omit<React.ComponentProps<"div">, "children" | "onChange"> & {
  title?: string;
  /** Without it the title is read-only text. */
  onTitleChange?: (title: string) => void;
  titlePlaceholder?: string;
  /** `drawer`: the smaller title of the task drawer. */
  size?: "page" | "drawer";
  width?: "prose" | "full";
  initialBlocks?: unknown[] | undefined;
  initialMarkdown?: string | undefined;
  placeholder?: string;
  editable?: boolean;
  onChange?: ((doc: PageDocument) => void) | undefined;
  /** Every document change, as it happens (typing, toolbar, menus), before `onChange`'s delay. */
  onEdit?: (() => void) | undefined;
  changeDelayMs?: number;
  mentionables?: MentionItem[];
  commands?: SlashCommand[];
  /** Pages offered after "[[" (a fixed list; `docLinks` replaces it). */
  pages?: PageLink[];
  /**
   * WIKI-LINKS-GRAPH: the [[ picker (search, "Create page") and the live titles of the doc links
   * it inserts.
   */
  docLinks?: DocLinks | undefined;
  uploadFile?: ((file: File) => Promise<string>) | undefined;
  /** Accessible name of the body. */
  label?: string;
};

export function PageEditor({
  title,
  onTitleChange,
  titlePlaceholder = "Untitled",
  size = "page",
  width = "prose",
  initialBlocks,
  initialMarkdown,
  placeholder = "Write, or press / for blocks…",
  editable = true,
  onChange,
  onEdit,
  changeDelayMs = 800,
  mentionables = NONE,
  commands = NONE,
  pages = NONE,
  docLinks,
  uploadFile,
  label = "Page body",
  className,
  ...props
}: PageEditorProps) {
  const [draft, setDraft] = React.useState(title ?? "");
  const titleRef = React.useRef<HTMLTextAreaElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => setDraft(title ?? ""), [title]);
  // Grow the title with its text (one row minimum).
  React.useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  });

  const titleClass = cn(
    "w-full resize-none overflow-hidden bg-transparent text-foreground outline-none placeholder:text-muted-foreground",
    size === "page"
      ? "font-heading text-2xl font-strong tracking-title max-md:text-xl"
      : "text-xl font-semibold tracking-heading",
  );

  return (
    <div
      data-slot="page-editor"
      data-width={width}
      className={cn("flex w-full flex-col gap-3", width === "prose" && "max-w-prose", className)}
      {...props}
    >
      {title !== undefined || onTitleChange ? (
        onTitleChange && editable ? (
          // The editable title is still the page's heading (its name is the field's value).
          <h1 className="flex">
            <textarea
              ref={titleRef}
              data-slot="page-editor-title"
              aria-label="Title"
              rows={1}
              value={draft}
              placeholder={titlePlaceholder}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => {
                const next = draft.trim();
                if (next && next !== title) onTitleChange(next);
                else setDraft(title ?? "");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  bodyRef.current?.querySelector<HTMLElement>("[contenteditable]")?.focus();
                } else if (e.key === "Escape") {
                  setDraft(title ?? "");
                  e.currentTarget.blur();
                }
              }}
              className={cn(titleClass, "rounded-sm focus-visible:outline-none")}
            />
          </h1>
        ) : (
          <h1 data-slot="page-editor-title" className={titleClass}>
            {title || titlePlaceholder}
          </h1>
        )
      ) : null}
      <div ref={bodyRef} className="-mx-1 min-h-32 max-md:mx-0">
        <React.Suspense
          fallback={
            <div
              role="status"
              aria-busy={true}
              aria-label="Loading the editor"
              className="flex flex-col gap-2 py-1"
            >
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          }
        >
          <PageEditorBody
            label={label}
            placeholder={placeholder}
            initialBlocks={initialBlocks}
            initialMarkdown={initialMarkdown}
            editable={editable}
            mentionables={mentionables}
            commands={commands}
            pages={pages}
            docLinks={docLinks}
            onChange={onChange}
            onEdit={onEdit}
            changeDelayMs={changeDelayMs}
            uploadFile={uploadFile}
          />
        </React.Suspense>
      </div>
    </div>
  );
}
